import React, { useEffect, useRef, useState } from "react";
import Range from "../Range";
import { FieldHintButton } from "./FieldHintButton";
import { getAccessibleFieldLabel } from "../utils/accessibility";

const formatRangeValue = (value, unit, postfix) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return value ?? "";
  }
  const formatted = num.toLocaleString("en-US");
  if (postfix) {
    return `${formatted}${postfix}`;
  }
  return unit ? `${formatted} ${unit}`.trim() : formatted;
};

/** Empty string must not become 0 via Number(""). */
const parseRangeNumber = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  const num = typeof value === "number" ? value : Number(String(value).trim());
  return Number.isFinite(num) ? num : null;
};

const resolveRangeValue = (currentValue, min, max, fallback = min) => {
  const parsed = parseRangeNumber(currentValue);
  if (parsed === null) {
    return fallback;
  }
  return Math.min(Math.max(parsed, min), max);
};

const VALUE_EASE_MS = 150;

export function RangeField({
  label,
  showLabel,
  required,
  hide,
  hint,
  hintStyle,
  startValue,
  endValue,
  postfix,
  uniqueId,
  uuid,
  currentValue,
  onChange,
  onLiveChange,
  onSeed,
}) {
  const min = Number(startValue?.value ?? 0);
  const max = Number(endValue?.value ?? 100);
  const safeMin = Number.isFinite(min) ? min : 0;
  const safeMax = Number.isFinite(max) ? max : 100;
  const resolvedValue = resolveRangeValue(
    currentValue,
    safeMin,
    safeMax,
    safeMin
  );

  const [displayValue, setDisplayValue] = useState(resolvedValue);
  const [isDragging, setIsDragging] = useState(false);
  const didSyncRef = useRef(false);
  const animatedValueRef = useRef(resolvedValue);
  const isDraggingRef = useRef(false);
  const animationFrameRef = useRef(null);
  const resolvedUniqueId = uniqueId || uuid;
  const labelId = resolvedUniqueId ? `${resolvedUniqueId}-label` : undefined;
  const plainLabel = getAccessibleFieldLabel(label, "Range");

  const cancelValueAnimation = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const setDisplayValueImmediate = (nextValue) => {
    cancelValueAnimation();
    animatedValueRef.current = nextValue;
    setDisplayValue(nextValue);
  };

  const animateDisplayValue = (targetValue) => {
    cancelValueAnimation();
    const startValue = animatedValueRef.current;
    if (startValue === targetValue) {
      return;
    }
    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min((now - startTime) / VALUE_EASE_MS, 1);
      const eased = progress * (2 - progress);
      const nextValue = startValue + (targetValue - startValue) * eased;
      animatedValueRef.current = nextValue;
      setDisplayValue(nextValue);
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else {
        animatedValueRef.current = targetValue;
        setDisplayValue(targetValue);
        animationFrameRef.current = null;
      }
    };

    animationFrameRef.current = requestAnimationFrame(step);
  };

  const updateDisplayValue = (nextValue, instant = false) => {
    if (instant || isDraggingRef.current) {
      setDisplayValueImmediate(nextValue);
      return;
    }
    animateDisplayValue(nextValue);
  };

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    updateDisplayValue(resolvedValue, isDraggingRef.current);
  }, [resolvedValue]);

  useEffect(() => {
    return () => {
      cancelValueAnimation();
    };
  }, []);

  useEffect(() => {
    if (didSyncRef.current || hide || !uuid) {
      return;
    }
    const parsed = parseRangeNumber(currentValue);
    if (parsed === null || parsed !== resolvedValue) {
      didSyncRef.current = true;
      if (typeof onLiveChange === "function") {
        onLiveChange(uuid, resolvedValue);
      }
      if (typeof onSeed === "function") {
        onSeed(uuid, resolvedValue);
      }
    } else {
      didSyncRef.current = true;
    }
  }, [currentValue, resolvedValue, hide, uuid, onLiveChange, onSeed]);

  if (hide) {
    return null;
  }

  const inputOpacity = hide ? 0.5 : 1;
  const hasVisibleLabel = showLabel !== false && Boolean(label);
  const sliderAriaLabelledBy =
    labelId && hasVisibleLabel ? [undefined, labelId] : undefined;
  const sliderAriaLabel = sliderAriaLabelledBy ? undefined : plainLabel || undefined;

  return (
    <div
      className="roi-form-group mb-3 position-relative"
      style={{ opacity: inputOpacity }}
      role="group"
      aria-labelledby={labelId && showLabel !== false && label ? labelId : undefined}
      aria-label={
        !labelId || showLabel === false ? plainLabel || undefined : undefined
      }
    >
      <div className="range-slider-header">
        <div className="range-slider-header__label">
          {showLabel !== false && label && (
            <label
              id={labelId}
              className="field-label range-slider-custom-label"
            >
              <span dangerouslySetInnerHTML={{ __html: label }} />{" "}
              {required && <span className="required">*</span>}
              <FieldHintButton
                fieldLabel={label}
                hint={hint}
                hintStyle={hintStyle}
              />
            </label>
          )}
          {showLabel === false && (
            <FieldHintButton
              fieldLabel={label}
              hint={hint}
              hintStyle={hintStyle}
            />
          )}
        </div>
        <div
          className={`range-slider-header__value${isDragging ? " is-dragging" : ""}`}
          aria-hidden="true"
        >
          {formatRangeValue(Math.round(displayValue), endValue?.unit, postfix)}
        </div>
      </div>

      <div className="range-slider-track">
        <Range
          startValue={startValue}
          endValue={endValue}
          uniqueId={resolvedUniqueId}
          uuid={uuid}
          currentValue={resolvedValue}
          ariaLabel={sliderAriaLabel}
          ariaLabelledBy={sliderAriaLabelledBy}
          onSeed={onSeed}
          onLiveChange={(fieldUuid, nextValue) => {
            updateDisplayValue(nextValue, isDraggingRef.current);
            if (typeof onLiveChange === "function") {
              onLiveChange(fieldUuid, nextValue);
            }
          }}
          onDragStateChange={setIsDragging}
          onCommit={onChange}
        />
      </div>

      <div className="range-slider-limits" aria-hidden="true">
        <span>{formatRangeValue(startValue?.value, startValue?.unit, postfix)}</span>
        <span>{formatRangeValue(endValue?.value, endValue?.unit, postfix)}</span>
      </div>
    </div>
  );
}
